'use client';

import { useRef, useEffect } from 'react';
import type { TotalsConfig, ReceiptSettings } from '@/lib/templates/schema';
import { SectionDivider } from './SectionDivider';

interface TotalsSectionProps {
  config: TotalsConfig;
  settings: ReceiptSettings;
}

/**
 * Renders inverted text (white-on-black) using a canvas element.
 * html2canvas copies <canvas> pixel data directly, so the export
 * always matches the builder preview.
 */
function InvertedValue({
  text,
  bgColor,
  fgColor,
  fontSize,
  fontFamily,
  fontWeight,
}: {
  text: string;
  bgColor: string;
  fgColor: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const font = `${fontWeight} ${fontSize}px ${fontFamily}`;

      // Measure text at 1x scale
      ctx.font = font;
      const metrics = ctx.measureText(text);
      const textWidth = Math.ceil(metrics.width) + 2;
      const textHeight = Math.ceil(fontSize * 1.35);

      // Use a fixed high scale for crisp export (html2canvas copies pixels directly)
      const scale = 4;
      canvas.width = textWidth * scale;
      canvas.height = textHeight * scale;
      canvas.style.width = `${textWidth}px`;
      canvas.style.height = `${textHeight}px`;

      // Disable smoothing for crisp pixel-font rendering
      ctx.imageSmoothingEnabled = false;
      ctx.scale(scale, scale);

      // Draw background
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, textWidth, textHeight);

      // Draw text
      ctx.font = font;
      ctx.fillStyle = fgColor;
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(text, 1, textHeight * 0.78);
    };

    // Wait for the custom font to load before drawing
    document.fonts.ready.then(draw);
  }, [text, bgColor, fgColor, fontSize, fontFamily, fontWeight]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: 'inline-block',
        verticalAlign: 'text-bottom',
      }}
    />
  );
}

export function TotalsSection({ config, settings }: TotalsSectionProps) {
  const formatValue = (value: string) => {
    // Display value exactly as entered (preserve all spaces)
    // If currency is 'none', just return the value as-is
    if (settings.currency === 'none') {
      return value;
    }
    
    // Add currency symbol
    return settings.currencyPosition === 'before'
      ? `${settings.currency}${value}`
      : `${value}${settings.currency}`;
  };

  // Label position as percentage (0 = far left, 100+ = overlap with values)
  const labelPosition = config.labelPosition ?? 50;
  const isNarrow = settings.spacing === 'narrow';
  const labelAlignment = config.labelAlignment || 'left'; // Default to left alignment for labels

  // Get transform based on label alignment
  // left: label starts at position (no transform)
  // center: label is centered at position
  // right: label ends at position
  const getLabelTransform = () => {
    switch (labelAlignment) {
      case 'center': return 'translateX(-50%)';
      case 'right': return 'translateX(-100%)';
      default: return 'none';
    }
  };

  return (
    <div className={`receipt-section ${isNarrow ? 'py-0.5' : 'py-2'}`}>
      <div className={isNarrow ? '' : 'space-y-0.5'} style={{ lineHeight: isNarrow ? 1.15 : 1.4 }}>
        {config.lines.map((line, index) => {
          const isTotal = line.isLarge || (index === config.lines.length - 1 && config.increaseTotalSize);
          const fontSize = isTotal && config.increaseTotalSize
            ? settings.fontSize * (1 + config.totalSizeIncrease / 100)
            : settings.fontSize;
          const isInverted = line.isInverted;

          // For inverted lines, only the value gets the inverted background (no extra padding/sizing)
          if (isInverted) {
            return (
              <div
                key={line.id}
                className="relative w-full text-right"
                style={{
                  fontSize,
                  fontFamily: settings.font,
                  fontWeight: line.isBold ? 'bold' : 'normal',
                }}
              >
                {/* Label positioned absolutely based on labelPosition and alignment */}
                <span 
                  className="whitespace-nowrap"
                  style={{ 
                    position: 'absolute',
                    left: `${labelPosition}%`,
                    transform: getLabelTransform(),
                  }}
                >
                  {line.label}
                </span>
                {/* Inverted value rendered via canvas for reliable html2canvas export */}
                <InvertedValue
                  text={formatValue(line.value)}
                  bgColor={settings.textColor}
                  fgColor={settings.backgroundColor}
                  fontSize={fontSize}
                  fontFamily={settings.font}
                  fontWeight={line.isBold ? 'bold' : 'normal'}
                />
              </div>
            );
          }

          return (
            <div
              key={line.id}
              className="relative w-full"
              style={{
                fontSize,
                fontFamily: settings.font,
                fontWeight: line.isBold ? 'bold' : 'normal',
              }}
            >
              {/* Label positioned absolutely based on labelPosition and alignment */}
              <span 
                className="whitespace-nowrap"
                style={{ 
                  position: 'absolute',
                  left: `${labelPosition}%`,
                  transform: getLabelTransform(),
                }}
              >
                {line.label}
              </span>
              {/* Value always at the right - whitespace-pre preserves spaces */}
              <span className="block text-right whitespace-pre">
                {formatValue(line.value)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Divider */}
      {config.showDivider && (
        <SectionDivider style={config.dividerStyle} settings={settings} />
      )}
    </div>
  );
}

